import { useState, useEffect } from 'react';
import axios from 'axios';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Users, RefreshCw, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';

export default function AgentListPanel() {
  const [agents, setAgents] = useState([]);
  const [filteredAgents, setFilteredAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (isOpen && agents.length === 0) {
      fetchAgents();
    }
  }, [isOpen]);

  useEffect(() => {
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const filtered = agents.filter(agent => 
        agent.first_name?.toLowerCase().includes(query) ||
        agent.last_name?.toLowerCase().includes(query) ||
        String(agent.npn || '').toLowerCase().includes(query) ||
        agent.status?.toLowerCase().includes(query)
      );
      setFilteredAgents(filtered);
    } else {
      setFilteredAgents(agents);
    }
  }, [searchQuery, agents]);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(
        'https://api1.simplyworkcrm.com/api:JBYRWqH3/portal/internal/contracting/allRecords'
      );
      setAgents(response.data || []);
      setFilteredAgents(response.data || []);
      toast.success(`Loaded ${response.data?.length || 0} agents`);
    } catch (err) {
      console.error('Error fetching agents:', err);
      setError('Failed to load agents');
      toast.error('Failed to load agents');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="fixed right-0 top-1/2 -translate-y-1/2 rounded-l-lg rounded-r-none z-40 shadow-lg border-r-0 hover:bg-[#d4af37]/10"
          title="View Agent List"
        >
          <Users className="w-4 h-4" />
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[500px] sm:w-[600px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-[#d4af37]" />
              Agent Directory
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchAgents}
              disabled={loading}
              className="rounded-lg"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </>
              )}
            </Button>
          </SheetTitle>
          <SheetDescription>
            View and search all agent records
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <Input
              type="text"
              placeholder="Search by name, NPN, or status..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="rounded-lg"
            />
          </div>

          {/* Agent Count */}
          <div className="flex items-center justify-between text-sm text-neutral-600">
            <span>
              Showing {filteredAgents.length} of {agents.length} agents
            </span>
          </div>

          {/* Error State */}
          {error && (
            <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          )}

          {/* Loading State */}
          {loading && agents.length === 0 && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#d4af37]" />
            </div>
          )}

          {/* Agents Table */}
          {!loading && filteredAgents.length > 0 && (
            <div className="border rounded-lg overflow-hidden">
              <div className="max-h-[calc(100vh-300px)] overflow-y-auto">
                <table className="w-full">
                  <thead className="bg-neutral-100 sticky top-0 z-10">
                    <tr>
                      <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3">Name</th>
                      <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3">NPN</th>
                      <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100 bg-white">
                    {filteredAgents.map((agent, index) => (
                      <tr key={agent.id || index} className="hover:bg-neutral-50">
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium text-neutral-800">
                            {agent.first_name} {agent.last_name}
                          </p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm font-mono text-neutral-700">
                            {agent.npn || 'N/A'}
                          </p>
                        </td>
                        <td className="px-4 py-3">
                          <Badge 
                            variant="outline"
                            className={`text-xs ${
                              agent.status?.toLowerCase() === 'active' 
                                ? 'border-green-300 text-green-700 bg-green-50' 
                                : agent.status?.toLowerCase() === 'inactive'
                                ? 'border-red-300 text-red-700 bg-red-50'
                                : 'border-neutral-300 text-neutral-700 bg-neutral-50'
                            }`}
                          >
                            {agent.status || 'N/A'}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Empty State */}
          {!loading && filteredAgents.length === 0 && agents.length > 0 && (
            <div className="text-center py-12 text-neutral-500">
              <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No agents found matching your search</p>
            </div>
          )}

          {!loading && agents.length === 0 && !error && (
            <div className="text-center py-12 text-neutral-500">
              <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No agents available</p>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchAgents}
                className="mt-4"
              >
                Load Agents
              </Button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
